import dayjs, { type Dayjs } from "dayjs"
import isSameOrAfter from "dayjs/plugin/isSameOrAfter"
import isSameOrBefore from "dayjs/plugin/isSameOrBefore"

import { type EntityWithoutRelations, type ClosingTimes, type MappedHolidays } from "@/types"
import {
  BookingDaysEnum,
  BookingTimeCategory,
  type CreateBookingInput,
  type BookingTypeObjectInput,
  type Booking,
  BookingTypeEnum,
  type BookingTypeObject,
  type BookingTimes,
  type Holiday,
  type InstitutionFilter,
  AppointmentType,
  type TimeRange,
} from "@/graphql/types"
import AppointmentsFilteredQuery from "@/graphql/appointment/AppointmentsFilteredList.gql"
import HolidaysInRangeQuery from "@/graphql/calendar/holidaysInRangeList.gql"
import { apolloClient } from "@/vue-apollo"

import {
  diffDateRanges,
  getDatesBetween,
  isWeekendName,
  formatDateToNaiveDate,
  type Year,
} from "./calendar"
import { isVacationBooking } from "./composables/bookingCalendar"
import { ensureDayjs, formatToRfc3339 } from "./date"
dayjs.extend(isSameOrBefore)
dayjs.extend(isSameOrAfter)

type Parent = {
  to?: string
  from: string
  bookingTypeLabel?: BookingTypeEnum
}

export type BookingFormModel = EntityWithoutRelations<
  Booking,
  "bookingDays" | "contract" | "section" | "institution" | "commune" | "organization" | "schoolData"
> & {
  childAgeUnder3: boolean
  bookingTypeLabel: BookingTypeEnum
  percentage?: number
  bookingDaysPolicy?: BookingDaysEnum
  bookingDaysCount: number
  communeId?: string
  slotSplitting: boolean
  section: string | null | undefined
  schoolData: {
    className?: string | null
    schoolId?: string | null
  }
}

export function determineTimeCategory(totalHours: number): BookingTimeCategory {
  if (totalHours > 12) {
    return BookingTimeCategory.Twelve
  } else if (totalHours > 11) {
    return BookingTimeCategory.Eleven
  } else if (totalHours > 10) {
    return BookingTimeCategory.Ten
  } else if (totalHours > 9) {
    return BookingTimeCategory.Nine
  } else if (totalHours > 8) {
    return BookingTimeCategory.Eight
  } else if (totalHours > 7) {
    return BookingTimeCategory.Seven
  } else if (totalHours > 6) {
    return BookingTimeCategory.Six
  } else if (totalHours > 5) {
    return BookingTimeCategory.Five
  } else if (totalHours > 4) {
    return BookingTimeCategory.Four
  } else if (totalHours > 3) {
    return BookingTimeCategory.Three
  } else if (totalHours > 2) {
    return BookingTimeCategory.Two
  } else if (totalHours > 1) {
    return BookingTimeCategory.One
  } else {
    return BookingTimeCategory.Zero
  }
}

export function getBookingType(
  type: BookingTypeEnum,
  data: {
    communeId?: string
    slotSplitting: boolean
    percent?: number
    bookingDaysPolicy?: BookingDaysEnum
    bookingDaysCount?: number
  }
): BookingTypeObjectInput | undefined {
  const {
    communeId,
    slotSplitting = false,
    percent = 0,
    bookingDaysPolicy = BookingDaysEnum.ManuallySet,
    bookingDaysCount = 0,
  } = data
  switch (type) {
    case BookingTypeEnum.Standard:
      return {
        standard: {
          communeId,
          slotSplitting,
        },
      }
    case BookingTypeEnum.Alternative_1:
      return {
        alternative1: {
          percent: percent,
        },
      }
    case BookingTypeEnum.Alternative_2:
      return {
        alternative2: {
          percent: percent,
        },
      }
    case BookingTypeEnum.ShortTime: {
      return {
        shortTime: {
          bookingDays: getBookingDaysCount(bookingDaysPolicy, bookingDaysCount).bookingDays,
          communeId,
        },
      }
    }
    case BookingTypeEnum.Vacation_1:
      return {
        vacation1: {
          bookingDays: getBookingDaysCount(bookingDaysPolicy, bookingDaysCount).bookingDays,
        },
      }
    case BookingTypeEnum.Vacation_2:
      return {
        vacation2: {
          bookingDays: getBookingDaysCount(bookingDaysPolicy, bookingDaysCount).bookingDays,
        },
      }
    case BookingTypeEnum.Vacation_3:
      return {
        vacation3: {
          bookingDays: getBookingDaysCount(bookingDaysPolicy, bookingDaysCount).bookingDays,
        },
      }
    default:
      return
  }
}

export function getBookingTypeEnum(bookingObject?: BookingTypeObjectInput): BookingTypeEnum {
  if (bookingObject?.standard) {
    return BookingTypeEnum.Standard
  } else if (bookingObject?.alternative1) {
    return BookingTypeEnum.Alternative_1
  } else if (bookingObject?.alternative2) {
    return BookingTypeEnum.Alternative_2
  } else if (bookingObject?.shortTime) {
    return BookingTypeEnum.ShortTime
  } else if (bookingObject?.vacation1) {
    return BookingTypeEnum.Vacation_1
  } else if (bookingObject?.vacation2) {
    return BookingTypeEnum.Vacation_2
  } else if (bookingObject?.vacation3) {
    return BookingTypeEnum.Vacation_3
  } else {
    return BookingTypeEnum.Standard
  }
}

export function getBookingTypeEnumFromObject(bookingObject?: BookingTypeObject): BookingTypeEnum {
  const type = bookingObject ? Object.keys(bookingObject)[0] : null

  switch (type) {
    case "standard":
      return BookingTypeEnum.Standard
    case "shortTime":
      return BookingTypeEnum.ShortTime
    case "alternative1":
      return BookingTypeEnum.Alternative_1
    case "alternative2":
      return BookingTypeEnum.Alternative_2
    case "vacation1":
      return BookingTypeEnum.Vacation_1
    case "vacation2":
      return BookingTypeEnum.Vacation_2
    case "vacation3":
      return BookingTypeEnum.Vacation_3
    case null:
    default:
      return BookingTypeEnum.Standard
  }
}

export function getBookingTypeLabel(booking: Booking | CreateBookingInput | undefined) {
  if (!booking) return BookingTypeEnum.Standard
  if ("type" in booking) {
    return booking.type
  } else {
    return getBookingTypeEnum(booking.typeObject)
  }
}

export function getBookingDaysCount(
  policy: BookingDaysEnum,
  bookingDaysCount: number,
  bookingDaysLength?: number
) {
  switch (policy) {
    case BookingDaysEnum.AccordingToCalendar:
      return {
        bookingDays: {
          accordingToCalendar: {
            ignoreThis: 0,
          },
        },
        value: bookingDaysLength ?? 0,
        disabled: true,
      }

    case BookingDaysEnum.AccordingToOpeningDays:
      return {
        bookingDays: {
          accordingToOpeningDays: {
            ignoreThis: 0,
          },
        },
        value: bookingDaysCount,
        disabled: true,
      }

    case BookingDaysEnum.ManuallySet: {
      return {
        bookingDays: {
          manuallySet: {
            days: bookingDaysCount,
          },
        },
        value: bookingDaysCount,
        disabled: false,
      }
    }
  }
}

/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
export const getTypeObjectProperty = (typeObject: Record<string, any>, property: string) => {
  if (typeObject && property in typeObject) {
    return typeObject[property]
  }
  return undefined
}

export const extractBookingDaysAndPolicy = (
  typeObject:
    | BookingTypeObject
    | BookingTypeObjectInput
    | undefined /*ShortTime | Vacation1 | Vacation2 | Vacation3*/
): { policy: BookingDaysEnum | undefined; days?: number } => {
  if (!typeObject) {
    return { policy: undefined, days: undefined }
  }

  if ("shortTime" in typeObject) {
    if (typeObject.shortTime?.bookingDays?.accordingToCalendar) {
      return { policy: BookingDaysEnum.AccordingToCalendar }
    }
    if (typeObject.shortTime?.bookingDays?.accordingToOpeningDays) {
      return { policy: BookingDaysEnum.AccordingToOpeningDays }
    }
    if (typeObject.shortTime?.bookingDays?.manuallySet) {
      return {
        policy: BookingDaysEnum.ManuallySet,
        days: typeObject.shortTime.bookingDays.manuallySet.days,
      }
    }
  } else if ("vacation1" in typeObject) {
    if (typeObject.vacation1?.bookingDays?.accordingToCalendar) {
      return { policy: BookingDaysEnum.AccordingToCalendar }
    }
    if (typeObject.vacation1?.bookingDays?.accordingToOpeningDays) {
      return { policy: BookingDaysEnum.AccordingToOpeningDays }
    }
    if (typeObject.vacation1?.bookingDays?.manuallySet) {
      return {
        policy: BookingDaysEnum.ManuallySet,
        days: typeObject.vacation1.bookingDays.manuallySet.days,
      }
    }
  } else if ("vacation2" in typeObject) {
    if (typeObject.vacation2?.bookingDays?.accordingToCalendar) {
      return { policy: BookingDaysEnum.AccordingToCalendar }
    }
    if (typeObject.vacation2?.bookingDays?.accordingToOpeningDays) {
      return { policy: BookingDaysEnum.AccordingToOpeningDays }
    }
    if (typeObject.vacation2?.bookingDays?.manuallySet) {
      return {
        policy: BookingDaysEnum.ManuallySet,
        days: typeObject.vacation2.bookingDays.manuallySet.days,
      }
    }
  } else if ("vacation3" in typeObject) {
    if (typeObject.vacation3?.bookingDays?.accordingToCalendar) {
      return { policy: BookingDaysEnum.AccordingToCalendar }
    }
    if (typeObject.vacation3?.bookingDays?.accordingToOpeningDays) {
      return { policy: BookingDaysEnum.AccordingToOpeningDays }
    }
    if (typeObject.vacation3?.bookingDays?.manuallySet) {
      return {
        policy: BookingDaysEnum.ManuallySet,
        days: typeObject.vacation3.bookingDays.manuallySet.days,
      }
    }
  } else if ("bookingDays" in typeObject) {
    return {
      policy: typeObject.bookingDaysEnum,
      ...("days" in typeObject.bookingDays ? { days: typeObject.bookingDays.days } : {}),
    }
  }
  return { policy: undefined, days: undefined }
}

export const extractBookingType = (
  typeObject: BookingTypeObjectInput | undefined
): BookingTypeEnum | undefined => {
  if (!typeObject) return undefined

  if ("standard" in typeObject) return BookingTypeEnum.Standard
  if ("shortTime" in typeObject) return BookingTypeEnum.ShortTime
  if ("alternative1" in typeObject) return BookingTypeEnum.Alternative_1
  if ("alternative2" in typeObject) return BookingTypeEnum.Alternative_2
  if ("vacation1" in typeObject) return BookingTypeEnum.Vacation_1
  if ("vacation2" in typeObject) return BookingTypeEnum.Vacation_2
  if ("vacation3" in typeObject) return BookingTypeEnum.Vacation_3
}

export const BookingTypePriority: { [key in BookingTypeEnum]: number } = {
  [BookingTypeEnum.Standard]: 1,
  [BookingTypeEnum.ShortTime]: 2,
  [BookingTypeEnum.Alternative_1]: 3,
  [BookingTypeEnum.Alternative_2]: 3,
  [BookingTypeEnum.Vacation_1]: 3,
  [BookingTypeEnum.Vacation_2]: 3,
  [BookingTypeEnum.Vacation_3]: 3,
} as const

export function sortBookings<T extends { typeObject?: BookingTypeObjectInput }>(a: T, b: T) {
  const atype = extractBookingType(a.typeObject)
  const btype = extractBookingType(b.typeObject)
  if (atype && btype) {
    return BookingTypePriority[atype] - BookingTypePriority[btype]
  }
  return 0
}

export function doesOverlapWithBookings(
  bookings: { type: BookingTypeEnum; from: string; to?: string }[],
  type: BookingTypeEnum,
  from: string,
  to: string | Date = new Date("9999-12-30")
): boolean {
  const fromDate = dayjs(from)
  const toDate = dayjs(to)

  return bookings.some((booking) => {
    if (booking.type !== type) return false

    const bookingFrom = dayjs(booking.from)
    const bookingTo = booking.to ? dayjs(booking.to) : dayjs("9999-12-30")

    return bookingFrom.isSameOrBefore(toDate) && bookingTo.isSameOrAfter(fromDate)
  })
}

export function doesOverlapWithAnyBooking(
  bookings: { type: BookingTypeEnum; from: string; to?: string }[],
  from: string,
  to: string | Date = new Date("9999-12-30")
): boolean {
  const fromDate = dayjs(from)
  const toDate = dayjs(to)

  return bookings.some((booking) => {
    const bookingFrom = dayjs(booking.from)
    const bookingTo = booking.to ? dayjs(booking.to) : dayjs("9999-12-30")

    return bookingFrom.isSameOrBefore(toDate) && bookingTo.isSameOrAfter(fromDate)
  })
}

export function overlappingValidation<
  T extends { to: string; from: string; type: BookingTypeEnum },
>(
  parent: Parent,
  processedBookings: T[],
  originalFrom?: string,
  originalTo?: string
): { isValid: boolean; message?: string } {
  const { bookingTypeLabel, from, to } = parent

  if (!processedBookings.length || !bookingTypeLabel) {
    return { isValid: true }
  }
  switch (bookingTypeLabel) {
    case BookingTypeEnum.Standard: {
      const containedBookings = processedBookings.filter(
        (booking) =>
          booking.type !== BookingTypeEnum.Standard &&
          dayjs(booking.from).isSameOrAfter(dayjs(originalFrom)) &&
          dayjs(booking.to).isSameOrBefore(dayjs(originalTo))
      )
      const allBookingsStillContained = containedBookings.every(
        (booking) =>
          dayjs(booking.from).isSameOrAfter(dayjs(from)) &&
          dayjs(booking.to).isSameOrBefore(dayjs(to))
      )
      if (!allBookingsStillContained) {
        return { isValid: false, message: "standard_must_contain_existing_bookings" }
      }

      // Check if Standard booking overlaps with a ShortTime booking
      const overlapsWithShortTime = doesOverlapWithBookings(
        processedBookings,
        BookingTypeEnum.ShortTime,
        from,
        to
      )

      if (overlapsWithShortTime) {
        return { isValid: false, message: "booking_overlaps_short_time" }
      }

      const overlapsWithStandard = doesOverlapWithBookings(
        processedBookings,
        BookingTypeEnum.Standard,
        from,
        to
      )
      if (overlapsWithStandard) {
        return { isValid: false, message: "booking_overlaps_standard_time" }
      }
      break
    }

    case BookingTypeEnum.ShortTime: {
      const overlapsWithAny = doesOverlapWithAnyBooking(processedBookings, from, to)
      if (overlapsWithAny) {
        return { isValid: false, message: "booking_no_overlap_allowed" }
      }
      break
    }

    case BookingTypeEnum.Alternative_1:
    case BookingTypeEnum.Alternative_2:
    case BookingTypeEnum.Vacation_1:
    case BookingTypeEnum.Vacation_2:
    case BookingTypeEnum.Vacation_3: {
      // Ensure a Standard booking fully contains this booking

      const overlapsWithShortTime = doesOverlapWithBookings(
        processedBookings,
        BookingTypeEnum.ShortTime,
        from,
        to
      )

      if (overlapsWithShortTime) {
        return { isValid: false, message: "booking_overlaps_short_time" }
      }

      const standardBookings = processedBookings.filter((b) => b.type === BookingTypeEnum.Standard)
      const mergedRanges = mergeDateRanges([...standardBookings])

      const variantBookingDateRangeIsValid = isOverlapping(mergedRanges, from, to)

      if (!variantBookingDateRangeIsValid) {
        return { isValid: false, message: "booking_must_be_fully_inside_standard" }
      }
      break
    }

    default:
      return { isValid: true }
  }

  return { isValid: true }
}

export function filterBookingsByContract(
  contractFrom: string,
  contractTo: string | null,
  bookingFrom: string,
  bookingTo: string | null
) {
  const contractFromDate = dayjs(contractFrom)
  const contractToDate = contractTo ? dayjs(contractTo) : null
  const bookingFromDate = dayjs(bookingFrom)
  const bookingToDate = bookingTo ? dayjs(bookingTo) : null

  if (!contractToDate) {
    if (!bookingToDate) {
      return bookingFromDate.isSameOrAfter(contractFromDate)
    }
    return bookingToDate.isSameOrAfter(contractFromDate)
  }

  if (!bookingToDate) {
    return bookingFromDate.isSameOrBefore(contractToDate)
  }

  return (
    bookingFromDate.isSameOrBefore(contractToDate) && bookingToDate.isSameOrAfter(contractFromDate)
  )
}

export type VacationBooking = Booking & {
  type: BookingTypeEnum.Vacation_1 | BookingTypeEnum.Vacation_2 | BookingTypeEnum.Vacation_3
}
export function findStandardBooking(booking: VacationBooking, bookings: Booking[]) {
  for (const maybeTheStandardBooking of bookings) {
    if (
      maybeTheStandardBooking.type === BookingTypeEnum.Standard &&
      isOverlapping([maybeTheStandardBooking], booking.from, booking.to)
    ) {
      return maybeTheStandardBooking
    }
  }
}

export function calculateTotalHours(times: BookingTimes): number {
  const calculateHours = (range: TimeRange | null | undefined): number => {
    if (!range) return 0

    const [fromHours, fromMinutes] = range.start.split(":").map(Number)
    const [toHours, toMinutes] = range.end.split(":").map(Number)

    const fromInMinutes = fromHours * 60 + fromMinutes
    const toInMinutes = toHours * 60 + toMinutes

    return (toInMinutes - fromInMinutes) / 60
  }

  let totalHours = 0
  const { comments: _c, __typename, ...weekdays } = times

  for (const day of Object.values(weekdays)) {
    if (day) {
      totalHours += calculateHours(day.am)
      totalHours += calculateHours(day.pm)
    }
  }

  return totalHours
}

export const splitHours = (timestring: string) => {
  const [h, m, s] = timestring.split(":")
  const hours = [parseInt(h), parseInt(m), parseInt(s)]
  if (hours.find((h) => isNaN(h))) return null
  return hours
}

function mergeDateRanges<T extends { from: string; to?: string }>(ranges: T[]): T[] {
  ranges.forEach((range) => {
    if (!range.to) {
      range.to = "9999-12-31"
    }
  })

  ranges.sort((a, b) => dayjs(a.from).diff(dayjs(b.from)))

  const mergedRanges: T[] = []
  let currentRange = { ...ranges[0] }

  for (let i = 1; i < ranges.length; i++) {
    const nextRange = ranges[i]

    if (dayjs(currentRange.to).add(1, "day").isSameOrAfter(dayjs(nextRange.from))) {
      currentRange.to = dayjs(currentRange.to).isAfter(dayjs(nextRange.to))
        ? currentRange.to
        : nextRange.to
    } else {
      mergedRanges.push(currentRange)
      currentRange = { ...nextRange }
    }
  }

  mergedRanges.push(currentRange)

  return mergedRanges
}

function isOverlapping<T extends { from: string; to?: string }>(
  mergedRanges: T[],
  from: string,
  to?: string
): boolean {
  const futureDate = dayjs("9999-12-31")

  const fromDate = dayjs(from)
  const toDate = to ? dayjs(to) : futureDate

  for (const range of mergedRanges) {
    const rangeFrom = dayjs(range.from)
    const rangeTo = range.to ? dayjs(range.to) : futureDate

    if (fromDate.isSameOrAfter(rangeFrom) && toDate.isSameOrBefore(rangeTo)) {
      return true
    }
  }

  return false
}

export function validateDate(
  holidays: MappedHolidays,
  closingTimes: ClosingTimes,
  date: Dayjs | string
) {
  const isoDate = typeof date === "string" ? date : date.format("YYYY-MM-DD")
  const isHoliday = holidays[isoDate]
  const isClosingTime = closingTimes[isoDate]
  const isWeekend = isWeekendName(ensureDayjs(date).day())
  return !isHoliday && !isClosingTime && !isWeekend
}

export function shouldUpdateBookingDays(
  current: BookingFormModel,
  initial: Partial<BookingFormModel> | undefined
) {
  const { bookingTypeLabel, bookingDaysPolicy, from, to } = current
  const {
    bookingTypeLabel: initialType,
    bookingDaysPolicy: initialPolicy,
    from: initialFrom,
    to: initialTo,
  } = initial ?? {}

  const policyChangedToOpening =
    initialPolicy !== BookingDaysEnum.AccordingToOpeningDays &&
    bookingDaysPolicy === BookingDaysEnum.AccordingToOpeningDays

  const policyChangedFromOpening =
    initialPolicy === BookingDaysEnum.AccordingToOpeningDays &&
    bookingDaysPolicy !== BookingDaysEnum.AccordingToOpeningDays

  const typeChangedToVacation =
    initialType && !isVacationBooking(initialType) && isVacationBooking(bookingTypeLabel)

  const typeChangedFromVacation =
    initialType && isVacationBooking(initialType) && !isVacationBooking(bookingTypeLabel)

  const typeChangedToShortTime =
    initialType &&
    BookingTypeEnum.ShortTime !== initialType &&
    BookingTypeEnum.ShortTime === bookingTypeLabel

  const typeChangedFromShortTime =
    initialType &&
    BookingTypeEnum.ShortTime === initialType &&
    BookingTypeEnum.ShortTime !== bookingTypeLabel

  const fromDateChanged = !dayjs(from).isSame(dayjs(initialFrom), "day")
  const toDateChanged = !dayjs(to).isSame(dayjs(initialTo), "day")

  const dateShortened =
    (initialFrom && from && dayjs(from).isAfter(dayjs(initialFrom))) ||
    (initialTo && to && dayjs(to).isBefore(dayjs(initialTo)))

  const dateLengthened =
    (initialFrom && from && dayjs(from).isBefore(dayjs(initialFrom))) ||
    (initialTo && to && dayjs(to).isAfter(dayjs(initialTo)))

  const onlyDateChanged =
    !policyChangedToOpening &&
    !policyChangedFromOpening &&
    !typeChangedToVacation &&
    !typeChangedFromVacation &&
    !typeChangedToShortTime &&
    !typeChangedFromShortTime &&
    (fromDateChanged || toDateChanged)

  const shouldSet =
    bookingDaysPolicy === BookingDaysEnum.AccordingToOpeningDays &&
    (isVacationBooking(bookingTypeLabel) || BookingTypeEnum.ShortTime === bookingTypeLabel) &&
    (policyChangedToOpening ||
      typeChangedToVacation ||
      typeChangedToShortTime ||
      (onlyDateChanged && dateLengthened))

  const shouldUnset =
    policyChangedFromOpening ||
    typeChangedFromVacation ||
    typeChangedFromShortTime ||
    (onlyDateChanged && dateShortened)

  return { shouldSet, shouldUnset, onlyDateChanged }
}

export function getValidDiffDates(
  holidays: MappedHolidays,
  closingTimes: ClosingTimes,
  from: string,
  to: string,
  initialFrom: string,
  initialTo: string
) {
  const diffDates = diffDateRanges(from, to, initialFrom, initialTo)
  return diffDates.filter((date) => validateDate(holidays, closingTimes, date))
}

export function getValidDatesInRange(
  holidays: MappedHolidays,
  closingTimes: ClosingTimes,
  from: string,
  to: string
) {
  return getDatesBetween(from, to).filter((date) => validateDate(holidays, closingTimes, date))
}

export async function fetchHolidaysAndClosings(context: InstitutionFilter, from: Date, to: Date) {
  if (!context || !from || !to) return { holidays: {}, closingTimes: {} }

  try {
    const [holidaysResult, appointmentsResult] = await Promise.all([
      apolloClient.query({
        query: HolidaysInRangeQuery,
        variables: { context, from: formatDateToNaiveDate(from), to: formatDateToNaiveDate(to) },
      }),
      apolloClient.query({
        query: AppointmentsFilteredQuery,
        variables: {
          context,
          filter: {
            from: formatToRfc3339(from),
            to: formatToRfc3339(to),
            type: [AppointmentType.Z, AppointmentType.Y],
          },
          includeEmployee: false,
        },
      }),
    ])

    const holidays: Record<string, Omit<Holiday, "date">> = {}
    for (const holiday of holidaysResult.data?.holidaysInRange || []) {
      const { date, ...rest } = holiday
      if (date) holidays[date] = rest
    }

    const closingTimes: ClosingTimes = {}
    for (const appointment of appointmentsResult.data?.appointmentsFiltered || []) {
      const { from, to, type, id } = appointment
      const daysInBetween = getDatesBetween(from, to)
      for (const date of daysInBetween) {
        if (!closingTimes[date]) {
          closingTimes[date] = { type, id }
        }
      }
    }

    return { holidays, closingTimes }
  } catch {
    return { holidays: {}, closingTimes: {} }
  }
}

export async function resolveHolidayAndClosingsDataForRange(
  from: Date,
  to: Date,
  year: Year,
  institutionFilter: InstitutionFilter,
  fallbackHolidays: MappedHolidays,
  fallbackClosingTimes: ClosingTimes
) {
  if (!year || dayjs(from).isBefore(year?.start) || dayjs(to).isAfter(year?.end)) {
    const { holidays, closingTimes } = await fetchHolidaysAndClosings(institutionFilter, from, to)

    return {
      holidays,
      closingTimes,
    }
  }

  return {
    holidays: fallbackHolidays,
    closingTimes: fallbackClosingTimes,
  }
}

export function getFilteredBookingDays(
  currentBooking: Booking | undefined,
  year: Year,
  from: Date,
  to: Date
) {
  if (!from) return []
  const bookingDays = currentBooking?.bookingDays ?? []
  if (!bookingDays.length) return []

  const yearStart = ensureDayjs(year.start)
  const yearEnd = ensureDayjs(year.end)
  const fromDate = from ? ensureDayjs(from) : null
  const toDate = to ? ensureDayjs(to) : null

  const rangeStart = fromDate?.isAfter(yearStart) ? fromDate : yearStart
  const rangeEnd = toDate ? (toDate.isAfter(yearEnd) ? yearEnd : toDate) : yearEnd
  if (!rangeStart.isValid() || !rangeEnd.isValid()) {
    return []
  }

  const filteredBookingDays = bookingDays.filter((b) => {
    const date = ensureDayjs(b.date)
    return date.isValid() && date.isBetween(rangeStart, rangeEnd, "date", "[]")
  })

  return filteredBookingDays
}
